import { getBackendUrl } from '../utils/backendUrl'
import {
  beginBrowserAuthentication,
  captureCsrfToken,
  clearCsrfToken,
} from './browserAuth'

const validateOAuthConfig = () => {
  const errors = []

  if (import.meta.env.DEV) {
    return {
      oauthAuthorizeUrl: '/oauth/login',
      clientId: import.meta.env.VITE_OAUTH_CLIENT_ID || null,
      redirectUri: import.meta.env.VITE_OAUTH_REDIRECT_URI || '/oauth/callback',
    }
  }

  const backendUrl = getBackendUrl()
  if (!backendUrl) {
    errors.push('VITE_BACKEND_URL is required for OAuth in production/staging')
  }

  const clientId = import.meta.env.VITE_OAUTH_CLIENT_ID
  if (!clientId) {
    errors.push('VITE_OAUTH_CLIENT_ID environment variable is required')
  }

  const appUrl = import.meta.env.VITE_APP_URL
  if (!appUrl) {
    errors.push('VITE_APP_URL environment variable is required')
  }

  if (errors.length > 0) {
    throw new Error(
      `OAuth configuration errors:\n${errors.map((e) => `  - ${e}`).join('\n')}\n\n` +
        'Please set the required environment variables in your .env file or environment configuration.',
    )
  }

  let redirectUri = import.meta.env.VITE_OAUTH_REDIRECT_URI
  if (!redirectUri) {
    try {
      const appUrlObj = new URL(appUrl)
      redirectUri = `${appUrlObj.origin}/oauth/callback`
    } catch {
      throw new Error(
        `Invalid VITE_APP_URL format: "${appUrl}". ` +
          'It must be a valid URL (e.g., https://admin.example.com).',
      )
    }
  }

  try {
    new URL(redirectUri)
  } catch {
    throw new Error(
      `Invalid VITE_OAUTH_REDIRECT_URI format: "${redirectUri}". ` +
        'It must be a valid URL (e.g., https://admin.example.com/oauth/callback).',
    )
  }

  const oauthAuthorizeUrl = `${backendUrl}/oauth/login`
  try {
    new URL(oauthAuthorizeUrl)
  } catch {
    throw new Error(
      `Invalid OAuth authorize URL: "${oauthAuthorizeUrl}". ` +
        'Check your VITE_BACKEND_URL configuration.',
    )
  }

  return {
    oauthAuthorizeUrl,
    clientId,
    redirectUri,
  }
}

const oauthConfig = validateOAuthConfig()
const OAUTH_AUTHORIZE_URL = oauthConfig.oauthAuthorizeUrl
const CLIENT_ID = oauthConfig.clientId
const REDIRECT_URI = oauthConfig.redirectUri

function b64url(bytes) {
  const bin = String.fromCharCode(...new Uint8Array(bytes))
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+/g, '')
}

function requireWebCrypto() {
  const webCrypto = globalThis.crypto

  if (
    !webCrypto ||
    typeof webCrypto.getRandomValues !== 'function' ||
    !webCrypto.subtle ||
    typeof webCrypto.subtle.digest !== 'function'
  ) {
    throw new Error('Secure browser cryptography is unavailable.')
  }

  return webCrypto
}

async function verifyWebCrypto() {
  const webCrypto = requireWebCrypto()
  await webCrypto.subtle.digest('SHA-256', new Uint8Array())
}

export async function createVerifier() {
  const webCrypto = requireWebCrypto()
  const buf = new Uint8Array(32)
  webCrypto.getRandomValues(buf)
  return b64url(buf.buffer)
}

export async function challengeFromVerifier(verifier) {
  const webCrypto = requireWebCrypto()
  const data = new TextEncoder().encode(verifier)
  const digest = await webCrypto.subtle.digest('SHA-256', data)
  return b64url(digest)
}

export function savePkce(state, verifier, challenge) {
  const key = `pkce:${state}`
  if (sessionStorage.getItem(key)) {
    return
  }
  sessionStorage.setItem(
    key,
    JSON.stringify({ verifier, challenge, t: Date.now() }),
  )
}

export function loadPkce(state) {
  const raw = sessionStorage.getItem(`pkce:${state}`)
  return raw ? JSON.parse(raw) : null
}

export function clearPkce(state) {
  sessionStorage.removeItem(`pkce:${state}`)
}

function generateRandomString() {
  const webCrypto = requireWebCrypto()
  const array = new Uint8Array(16)
  webCrypto.getRandomValues(array)
  return b64url(array)
}

export async function startAuthFlow() {
  if (!CLIENT_ID) {
    throw new Error(
      'VITE_OAUTH_CLIENT_ID environment variable is required. ' +
        'Please set it in your .env file or environment configuration.',
    )
  }

  beginBrowserAuthentication()
  await verifyWebCrypto()

  const state = generateRandomString()
  const verifier = await createVerifier()
  const challenge = await challengeFromVerifier(verifier)

  savePkce(state, verifier, challenge)

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    scope: '*',
    state: state,
  })

  window.location.href = OAUTH_AUTHORIZE_URL + '?' + params.toString()
}

export async function exchangeCodeForTokens(code, state) {
  beginBrowserAuthentication()

  if (!code || !state) {
    throw new Error('Missing code or state')
  }

  if (!CLIENT_ID) {
    throw new Error(
      'VITE_OAUTH_CLIENT_ID environment variable is required. ' +
        'Please set it in your .env file or environment configuration.',
    )
  }

  const pkce = loadPkce(state)
  if (!pkce) {
    throw new Error('Missing PKCE for this state (likely a regen/overwrite)')
  }

  let recomputed
  try {
    recomputed = await challengeFromVerifier(pkce.verifier)
  } catch (error) {
    clearPkce(state)
    throw error
  }

  if (recomputed !== pkce.challenge) {
    throw new Error('PKCE mismatch before exchange (verifier overwritten)')
  }

  const exchangeUrl = import.meta.env.DEV
    ? '/api/auth/exchange'
    : `${getBackendUrl()}/api/auth/exchange`

  try {
    const response = await fetch(exchangeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        code: code,
        code_verifier: pkce.verifier,
        redirect_uri: REDIRECT_URI,
        client_id: CLIENT_ID,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(
        data.error_description || data.error || 'Token exchange failed',
      )
    }

    if (data?.success !== true || !data.user || typeof data.user !== 'object') {
      throw new Error('Token exchange returned an invalid response')
    }

    captureCsrfToken(data, response.headers)
    clearPkce(state)

    return data
  } catch (error) {
    clearCsrfToken()
    clearPkce(state)
    throw error
  }
}
